if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        /* Capture-phase click guard. Third-party apps (e.g. Upcart) attach
           their own click listeners that call /cart/add.js directly, bypassing
           the form's submit event entirely. Validating here, in capture, lets
           us preventDefault + stopImmediatePropagation before those handlers
           run. The form-submit guard in onSubmitHandler is still a useful
           secondary check (Shop Pay, requestSubmit(), keyboard Enter, etc). */
        this.submitButton.addEventListener('click', this.onSubmitClick.bind(this), { capture: true });

        /* Apply initial gating. MFQ's inline scripts use jQuery(...).ready()
           but jQuery is loaded with defer, so on initial load MFQ's "if input
           empty, set disabled" code throws ReferenceError silently and never
           runs. By the time variant change triggers viewTransition's script
           re-injection, jQuery has loaded and MFQ behaves normally. Applying
           our own gating here patches the load-order bug consistently. */
        this.reevaluateGating = this.reevaluateGating.bind(this);
        this.bindMarkerObserver = this.bindMarkerObserver.bind(this);

        this.reevaluateGating();
        this.bindMarkerObserver();

        /* Follow the colour tool's own events as well. The marker observer
           can miss a change: a variant re-render swaps the marker element
           after the observer re-bound to the previous one, and a clear
           happens through inputs the observer does not watch. Every select
           path dispatches mfq:color-selected and the shared clear routine
           dispatches mfq:color-cleared, so re-gating on those keeps the
           button in step with the inputs — never locked with a colour on
           screen, never open without one. Bound once for the page: the
           popular swatches fire before they fill the inputs, hence the
           deferred read (same reason as vfs-kleurbasis). */
        if (!window.__vfsColourGateBound) {
          window.__vfsColourGateBound = true;
          ['mfq:color-selected', 'mfq:color-cleared'].forEach((name) => {
            document.addEventListener(name, () => {
              setTimeout(() => {
                document.querySelectorAll('product-form').forEach((pf) => {
                  if (typeof pf.reevaluateGating === 'function') pf.reevaluateGating();
                });
              }, 0);
            });
          });
        }

        /* viewTransition replaces variant-selects on variant change, which
           swaps the color marker for a new element. Re-bind the observer
           when that happens so we keep tracking the live marker, then
           re-evaluate (the new marker may be empty and need re-disabling). */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            setTimeout(() => {
              /* data-unavailable reflects the server-rendered variant only.
                 After a variant change product-info owns availability state,
                 so drop the stale marker before re-gating. */
              this.submitButton.removeAttribute('data-unavailable');
              this.bindMarkerObserver();
              this.reevaluateGating();
            }, 100);
          });
        }
      }

      /* Single source of truth for gating state. Runs on initial load, on
         marker mutations (color selection), and after variant change. */
      reevaluateGating() {
        const unfilled = this.findFirstUnfilledStep();
        if (unfilled) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('data-color-gated', '');
          this.handleErrorMessage('Selecteer een kleur om verder te gaan.');
        } else {
          /* Only lift a lock we own. Liquid ships colour-gated buttons as
             disabled + data-color-gated so a page load where scripts never
             run (VS17207) cannot add colourless mixing paint. Buttons that
             are disabled WITHOUT that marker (data-unavailable / sold-out
             state set by Liquid or product-info) belong to the availability
             logic and must stay locked. */
          const wasColorGated = this.submitButton.hasAttribute('data-color-gated');
          if (wasColorGated) {
            this.submitButton.removeAttribute('data-color-gated');
            if (!this.submitButton.hasAttribute('data-unavailable')) {
              this.submitButton.removeAttribute('disabled');
            }
          }
          this.handleErrorMessage();
        }
      }

      /* MFQ fills #mfq_popup_selected_product_color text when the user picks
         a color. Re-bindable: variant change replaces the marker element via
         viewTransition, and we need to track the new one. */
      bindMarkerObserver() {
        if (this._markerObserver) this._markerObserver.disconnect();
        const marker = document.getElementById('mfq_popup_selected_product_color');
        if (!marker) return;
        this._markerObserver = new MutationObserver(this.reevaluateGating);
        this._markerObserver.observe(marker, { childList: true, characterData: true, subtree: true });
      }

      onSubmitClick(evt) {
        if (this.submitButton.disabled) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const unfilled = this.findFirstUnfilledStep();
        if (!unfilled) {
          // Validation passed — clear any prior "select a color" message.
          this.handleErrorMessage();
          return;
        }

        evt.preventDefault();
        evt.stopImmediatePropagation();

        unfilled.scrollIntoView({ behavior: 'smooth', block: 'start' });
        unfilled.style.boxShadow = '0 0 0 2px #ba0066';
        unfilled.style.borderRadius = unfilled.style.borderRadius || '8px';
        setTimeout(() => { unfilled.style.boxShadow = ''; }, 2500);
        this.handleErrorMessage('Selecteer een kleur om verder te gaan.');
      }

      /* Returns the first unfilled selection element, or null if all required
         selections are present. Walks AKTA's .variant-step-card[data-step]
         cards when present, then falls back to the legacy MFQ Kleur input. */
      findFirstUnfilledStep() {
        const cards = document.querySelectorAll('.variant-step-card[data-step]');
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i];

          // Single-option steps don't need user action.
          const optionCount = parseInt(card.getAttribute('data-option-values-count'), 10);
          if (!isNaN(optionCount) && optionCount <= 1) continue;

          /* The light/dark price group ("Basis") is filled from the chosen
             colour, never by the shopper, and its card is hidden. It always
             has a value, so it can never legitimately block submission — but
             returning it would scroll the page to nothing. */
          if (card.classList.contains('variant-step-card--hidden')) continue;

          // Color step: identified by presence of marker or kleur input in the card.
          const colorMarker = card.querySelector('#mfq_popup_selected_product_color');
          const colorInput = card.querySelector('.mfq_popup_selected_color');
          if (colorMarker || colorInput) {
            /* Gate strictly on the SUBMITTED value. The order's Kleur line-item
               property comes ONLY from the form's hidden input
               (properties[Kleur], filled via $('.mfq_popup_selected_color') on
               selection). The visible marker (#mfq_popup_selected_product_color)
               is UI feedback only — and there are two of them (a header copy is
               FIRST in DOM, so getElementById reads that one), so the marker can
               read "selected" while the form input is still empty. Previously we
               passed when the marker OR the input was filled, so that desync
               slipped through as a colourless paid order (root cause of VS16847).
               Requiring the input closes the hole without over-blocking: every
               real selection sets this input alongside the marker via
               showSelectedState(). */
            const formInput = this.form && this.form.querySelector('.mfq_popup_selected_color');
            const formInputHasValue = formInput && formInput.value && formInput.value.trim() !== '';
            if (!formInputHasValue) return card;
            continue;
          }

          // Radio / checkbox variant step.
          if (card.querySelector('input[type="radio"], input[type="checkbox"]')) {
            if (!card.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) {
              return card;
            }
            continue;
          }

          // Dropdown variant step.
          const select = card.querySelector('select');
          if (select && !select.value) return card;
        }

        /* Legacy PDP fallback: MFQ renders a hidden input named properties[Kleur]
           only when the product has a color_menu metafield. We check BOTH the
           input value and the visible color marker (#mfq_popup_selected_product_color)
           because the input can carry a stale localStorage value from a
           previous session while the user-visible UI shows nothing selected.
           Marker is the user-facing source of truth; if it's empty, block.

           Only runs when no AKTA cards exist — those already handle the color
           step via the marker, and running both creates a race where MFQ fills
           the marker before the input (which fires our observer too early). */
        const legacyColorInput = cards.length === 0 && this.form.querySelector('.mfq_popup_selected_color');
        if (legacyColorInput) {
          const marker = document.getElementById('mfq_popup_selected_product_color');
          const markerHasText = marker && marker.textContent.trim() !== '';
          const inputHasValue = legacyColorInput.value && legacyColorInput.value.trim() !== '';
          if (!markerHasText || !inputHasValue) {
            /* Scope to product-info so we don't pick up the header's color
               picker (renders the same snippet) — that would scroll past
               the variant section to a wrong target. Prefer the wrapping
               div around the marker so the "Kleur:" label and the
               popular-colors block both come into view. */
            const productInfo = this.closest('product-info');
            const root = productInfo || document;
            const sectionWrap = marker && marker.closest('div');
            return sectionWrap
                || root.querySelector('.uf-color-picker-row')
                || root.querySelector('#mfq_populer_color_main_wrapper')
                || root.querySelector('.color_popup_trg')
                || legacyColorInput;
          }
        }

        return null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        /* Defense-in-depth: validate every variant step before allowing the
           cart-add. This catches:
            - The AKTA PDP gate (which used to be a visual overlay; now removed).
            - Any programmatic submit path that bypasses the inline form guard.
            - Dynamic-checkout / Shop Pay button paths that submit the form.
           No-op on legacy PDPs (no step cards rendered). */
        const unfilled = this.findFirstUnfilledStep();
        if (unfilled) {
          unfilled.scrollIntoView({ behavior: 'smooth', block: 'center' });
          unfilled.style.boxShadow = '0 0 0 2px #ba0066';
          unfilled.style.borderRadius = unfilled.style.borderRadius || '8px';
          setTimeout(() => { unfilled.style.boxShadow = ''; }, 2500);
          this.handleErrorMessage('Selecteer een kleur om verder te gaan.');
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            /* Network failure, malformed response, 5xx — surface to the user
               instead of swallowing. Without this, a failed ATC looked like a
               dead button: spinner blinked, nothing changed, customer left. */
            console.error('product-form cart-add failed', e);
            const fallbackMessage =
              (window.variantStrings && window.variantStrings.error) ||
              'Sorry, er ging iets mis. Probeer het opnieuw.';
            this.handleErrorMessage(fallbackMessage);
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'cart_add_error', error: String(e) });
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const spinnerEl = this.querySelector('.loading__spinner');
            if (spinnerEl) spinnerEl.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

    }
  );
}
